import { z } from 'zod';
import { uuid } from './posting.js';
/**
 * Die Antwortverträge der API-Token-Verwaltung — `C-tokenvertrag` (2026-09-01).
 *
 * `apps/api/src/apitokens/apitokens.service.ts` führte
 * `ApiTokenSummary`/`CreatedApiToken`/`ApiTokenPermissionView` bislang als
 * dienstlokale `interface`-Formen, so wie es `users.service.ts` bis
 * `V-vertrag3` auch tat (dort das Vorbild) — der OpenAPI-Erzeuger blieb an
 * `GET /api/v1/apitokens`, `POST /api/v1/apitokens` und
 * `PUT /api/v1/apitokens/:id/permissions` deshalb `[UNGEKLÄRT]`.
 *
 * `apps/web/src/api/schemas.ts` spiegelte dieselbe Form ein zweites Mal
 * (Auftrag `W-apitoken-ui`, mit eigenem Kopfkommentar, der genau diesen Zustand
 * als gemessenen Befund benannte) — dieser Spiegel entfällt mit diesem Vertrag.
 *
 * **`method` bleibt `z.string()` und wird kein `z.enum(...)`.** Der
 * Wertevorrat steht im Controller (`methodSchema` in
 * `apitokens.controller.ts`) und nicht im Antwortvertrag — derselbe Grund,
 * den der Web-Spiegel vor diesem Auftrag schon nannte: Die Anzeige braucht
 * ihn nicht als geschlossene Menge, sie zeigt, was der Server geschrieben
 * hat.
 *
 * **`apiTokenSetPermissionsRequestSchema` kommt mit `W-tokenrechte`
 * (2026-09-26) dazu** — der ANFRAGE-Rumpf von
 * `PUT /api/v1/apitokens/:id/permissions` lebte bis dahin ausschliesslich als
 * `setPermissionsSchema` im Controller; die Oberfläche konnte ihn deshalb nur
 * nachbauen statt zu teilen. Er trägt hier absichtlich eine EIGENE, engere
 * `method`-Form (`z.enum(...)`) statt `apiTokenPermissionViewSchema`
 * wiederzuverwenden: Ein Antwortvertrag zeigt, was der Server geschrieben
 * hat, ein Anfragevertrag muss vor dem Absenden ausschliessen, was er gar
 * nicht erst annimmt — dieselbe Trennung wie im Controller selbst.
 */
/** Eine Endpunktberechtigung eines Tokens, wie sie nach aussen geht — mit ihren Verwaltungsangaben. */
export const apiTokenPermissionViewSchema = z
    .object({
    method: z.string(),
    path: z.string(),
    grantedAt: z.string(),
    grantedBy: z.string(),
})
    .strict();
/**
 * Die Antworthülle von `PUT /api/v1/apitokens/:id/permissions` — `{ permissions: [...] }`. Der Umschlag lebt neben seinem
 * Elementschema, damit Server und Oberfläche dieselbe Hülle prüfen (Handbuch §3.969).
 */
export const apiTokenPermissionListResponseSchema = z.object({ permissions: z.array(apiTokenPermissionViewSchema).readonly() }).strict();
/** Ein Token, wie die Liste es zeigt — ohne den Wert, den es nie zurückgibt. */
export const apiTokenSummarySchema = z
    .object({
    id: uuid,
    beschreibung: z.string(),
    /** Die Wiedererkennung ohne Klartext — `api_token.token_prefix`. */
    prefix: z.string(),
    createdAt: z.string(),
    createdBy: z.string(),
    expiresAt: z.string().nullable(),
    revokedAt: z.string().nullable(),
    revokedBy: z.string().nullable(),
    permissions: z.array(apiTokenPermissionViewSchema).readonly(),
})
    .strict();
/**
 * Die Antworthülle von `GET /api/v1/apitokens` — `{ tokens: [...] }`. Der Umschlag lebt neben seinem
 * Elementschema, damit Server und Oberfläche dieselbe Hülle prüfen (Handbuch §3.969).
 */
export const apiTokenListResponseSchema = z.object({ tokens: z.array(apiTokenSummarySchema).readonly() }).strict();
/**
 * Das Ergebnis von `POST /api/v1/apitokens` — der Tokenwert steht genau
 * hier und nie wieder. Gespeichert wird ausschliesslich sein SHA-256-Hash,
 * einen zweiten Abrufweg gibt es nicht, dieselbe Zusage wie beim
 * Einmalpasswort aus `POST /api/v1/users` (`packages/contracts/src/users.ts`,
 * `createdUserSchema`).
 */
export const createdApiTokenSchema = apiTokenSummarySchema.extend({ value: z.string() });
/**
 * Der Rumpf von `PUT /api/v1/apitokens/:id/permissions` — die
 * **vollständige** Liste der Endpunktberechtigungen, die ein Token danach
 * trägt. Kein Anhängen: Was hier fehlt, wird entzogen; ein leerer
 * `permissions`-Wert ist zulässig (`min(0)`) und entzieht damit jede
 * Berechtigung — dieselbe Zusage wie im Controller
 * (`apps/api/src/apitokens/apitokens.controller.ts`, `setPermissions()`).
 */
export const apiTokenSetPermissionsRequestSchema = z
    .object({
    permissions: z
        .array(z.object({
        method: z.enum(['GET', 'POST', 'PATCH', 'PUT', 'DELETE']),
        path: z.string().trim().min(1, 'Pfad fehlt').startsWith('/', 'Der Pfad beginnt mit "/"'),
    }))
        .min(0),
})
    .strict();
//# sourceMappingURL=apitokens.js.map